import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';
import type {
  FanClubTierSummary,
  GetModelAnalyticsResponse,
  MediaItemSummary,
  WalletSummaryResponse,
} from '@fetmode/types';
import { useAuth } from '@fetmode/app-core';
import { t, getLocale } from '@fetmode/i18n';
import { formatCoins, formatUsdCents } from '@fetmode/utils';

import { SettingsStack } from './SettingsStack.js';

interface AnalyticsBundle {
  wallet: WalletSummaryResponse | null;
  media: readonly MediaItemSummary[];
  fanClubTiers: readonly FanClubTierSummary[];
  affiliateReferrals: number | null;
  analytics: GetModelAnalyticsResponse | null;
}

function countryLabel(code: string): string {
  try {
    return new Intl.DisplayNames([getLocale() === 'es' ? 'es' : 'en'], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

function formatHourLabel(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return new Intl.DateTimeFormat(getLocale() === 'es' ? 'es' : 'en', {
    hour: 'numeric',
  }).format(date);
}

function BarRow({ label, value, max }: { label: string; value: number; max: number }): ReactElement {
  const width = max <= 0 ? 0 : Math.round((value / max) * 100);
  return (
    <div className="studio-analytics-bar">
      <span className="studio-analytics-bar__label">{label}</span>
      <div className="studio-analytics-bar__track" aria-hidden="true">
        <div className="studio-analytics-bar__fill" style={{ width: `${String(width)}%` }} />
      </div>
      <span className="studio-analytics-bar__value">{formatCoins(value)}</span>
    </div>
  );
}

export function SettingsAnalyticsPage(): ReactElement {
  const { api } = useAuth();
  const [data, setData] = useState<AnalyticsBundle | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void Promise.allSettled([
      api.getWallet(),
      api.listModelMedia(),
      api.listMyFanClubTiers(),
      api.getModePassAffiliateStats(),
      api.getModelAnalytics(),
    ]).then(([wallet, media, tiers, affiliate, analytics]) => {
      if (cancelled) {
        return;
      }
      setData({
        wallet: wallet.status === 'fulfilled' ? wallet.value : null,
        media: media.status === 'fulfilled' ? media.value.items : [],
        fanClubTiers: tiers.status === 'fulfilled' ? tiers.value.tiers : [],
        affiliateReferrals: affiliate.status === 'fulfilled' ? affiliate.value.referralCount : null,
        analytics: analytics.status === 'fulfilled' ? analytics.value : null,
      });
      if (
        wallet.status === 'rejected' &&
        media.status === 'rejected' &&
        tiers.status === 'rejected' &&
        analytics.status === 'rejected'
      ) {
        setError(t('common.studio.analytics.loadError'));
      }
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const publishedCount = data?.media.filter((item) => item.status === 'PUBLISHED').length ?? 0;
  const enabledTiers = data?.fanClubTiers.filter((tier) => tier.enabled).length ?? 0;
  const earn = data?.wallet?.earn ?? null;
  const totalEarnCents = earn ? earn.availableCents + earn.pendingCents : 0;
  const hourlyMax = useMemo(
    () => Math.max(1, ...(data?.analytics?.hourlyTips.map((row) => row.coins) ?? [0])),
    [data?.analytics?.hourlyTips],
  );

  return (
    <SettingsStack titleKey="common.studio.settings.analyticsTitle" hintKey="common.studio.settings.analyticsHint">
      {isLoading ? (
        <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.analytics.loading')}</p>
      ) : null}
      {error ? <p className="fm-error">{error}</p> : null}

      {!isLoading ? (
        <>
          <div className="fm-card">
            <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.analytics.hourlyTitle')}</h2>
            {data?.analytics && data.analytics.hourlyTips.some((row) => row.coins > 0) ? (
              <div className="studio-analytics-bars">
                {data.analytics.hourlyTips.map((row) => (
                  <BarRow key={row.hour} label={formatHourLabel(row.hour)} value={row.coins} max={hourlyMax} />
                ))}
              </div>
            ) : (
              <p style={{ margin: 0, color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
                {t('common.studio.analytics.hourlyEmpty')}
              </p>
            )}
          </div>

          <div className="fm-card">
            <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.analytics.peakTitle')}</h2>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.875rem' }}>
              {t('common.studio.analytics.allTimePeak').replace(
                '{{count}}',
                String(data?.analytics?.allTimePeak ?? 0),
              )}
            </p>
            {data?.analytics && data.analytics.peakViewers.some((row) => row.peakViewerCount > 0) ? (
              <ul className="studio-analytics-list">
                {data.analytics.peakViewers
                  .filter((row) => row.peakViewerCount > 0)
                  .map((row) => (
                    <li key={row.showId}>
                      <span>{new Date(row.startedAt).toLocaleString()}</span>
                      <span>
                        {row.type} · {String(row.peakViewerCount)}
                      </span>
                    </li>
                  ))}
              </ul>
            ) : (
              <p style={{ margin: 0, color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
                {t('common.studio.analytics.peakEmpty')}
              </p>
            )}
          </div>

          <div className="fm-card">
            <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.analytics.geoTitle')}</h2>
            {data?.analytics && data.analytics.geography.length > 0 ? (
              <ul className="studio-analytics-list">
                {data.analytics.geography.map((row) => (
                  <li key={row.countryCode}>
                    <span>{countryLabel(row.countryCode)}</span>
                    <span>
                      {t('common.studio.analytics.geoViewers').replace('{{count}}', String(row.viewerCount))}
                      {row.tipCoins > 0 ? ` · ${formatCoins(row.tipCoins)}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ margin: 0, color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
                {t('common.studio.analytics.geoEmpty')}
              </p>
            )}
          </div>

          <div className="fm-card">
            <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.analytics.earningsTitle')}</h2>
            {earn ? (
              <dl className="studio-analytics-dl">
                <div>
                  <dt>{t('common.studio.dashboard.earnAvailable')}</dt>
                  <dd>{formatUsdCents(earn.availableCents)}</dd>
                </div>
                <div>
                  <dt>{t('common.studio.dashboard.earnPending')}</dt>
                  <dd>{formatUsdCents(earn.pendingCents)}</dd>
                </div>
                <div>
                  <dt>{t('common.studio.analytics.earnTotal')}</dt>
                  <dd>{formatUsdCents(totalEarnCents)}</dd>
                </div>
              </dl>
            ) : (
              <p style={{ margin: 0, color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
                {t('common.studio.analytics.earnEmpty')}
              </p>
            )}
          </div>

          <div className="fm-card">
            <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.analytics.catalogTitle')}</h2>
            <dl className="studio-analytics-dl">
              <div>
                <dt>{t('common.studio.analytics.mediaPublished')}</dt>
                <dd>
                  {String(publishedCount)} / {String(data?.media.length ?? 0)}
                </dd>
              </div>
              <div>
                <dt>{t('common.studio.analytics.fanClubTiers')}</dt>
                <dd>{String(enabledTiers)}</dd>
              </div>
              {data?.affiliateReferrals != null ? (
                <div>
                  <dt>{t('common.studio.analytics.affiliateReferrals')}</dt>
                  <dd>{String(data.affiliateReferrals)}</dd>
                </div>
              ) : null}
              {data?.wallet ? (
                <div>
                  <dt>{t('common.studio.dashboard.spendBalance')}</dt>
                  <dd>{formatCoins(data.wallet.spend.total)}</dd>
                </div>
              ) : null}
            </dl>
          </div>
        </>
      ) : null}
    </SettingsStack>
  );
}
